import React from 'react'

import { ZariContext } from '_zari/context'
import { Portlet, Row, Col, Button } from '_zari/core'

import { CodigosTransaccionesConstant, MesesContabilidadConstant } from 'constants/index'

import { RounderUtil, DownloadAxiosFile } from 'utils'

import * as PresupuestoAsignacionService from 'services/presupuestoAsignacion.service'

import * as Cells from './cells'

const MainTable = ({ presupuestosDatos, idProyecto, anoDesde, datoNivel1 }) => {
  const __zariContext = React.useContext(ZariContext)

  const [importesAsignadosBd, setImportesAsignadosBd] = React.useState(null)

  const [totalesPeriodos, setTotalesPeriodos] = React.useState({})
  const [totalesPresupuestos, setTotalesPresupuestos] = React.useState({})

  const infoPeriodos = React.useMemo(() => {
    const anoActual = new Date().getFullYear()

    const salida = []

    for (let ano = anoDesde; ano <= anoActual; ano++) {
      for (let j = 0; j < MesesContabilidadConstant.__arrayEneDic.length; j++) {
        const infoMes = MesesContabilidadConstant.__arrayEneDic[j]

        salida.push({
          anoMes: `${ano}${infoMes.id}`,
          nombre: `${infoMes.nombreCorto}-${ano}`
        })
      }
    }

    return salida
  }, [anoDesde])

  const obtenerImportesAsignados = React.useCallback(() => {
    return new Promise((res, rej) => {
      PresupuestoAsignacionService.obtenerAsInfoAsignacion({ idProyecto, anoDesde, datoNivel1 })
        .then(({ data }) => {
          setImportesAsignadosBd(data)
          res(data)
        })
        .catch(() => {
          setImportesAsignadosBd([])
        })
    })
  }, [idProyecto, anoDesde, datoNivel1])

  const limpiarTotales = React.useCallback(() => {
    setTotalesPeriodos({})
    setTotalesPresupuestos({})
  }, [])

  const clickTotalizar = React.useCallback(() => {
    obtenerImportesAsignados().then(dataImportes => {
      // Calcular totales periodos
      let salidaPeriodos = {}
      for (let i = 0; i < infoPeriodos.length; i++) {
        const infoPeriodo = infoPeriodos[i]

        const asignadosxAnoMes = dataImportes.filter(x => x.anoMes === infoPeriodo.anoMes)
        const sumaAsignadosxAnoMes = asignadosxAnoMes.reduce(
          (suma, current) => RounderUtil.round(suma + (current.importe || 0)),
          0
        )

        salidaPeriodos[infoPeriodo.anoMes] = sumaAsignadosxAnoMes || 0
      }

      let salidaPresupuestoDato = {}
      // Calcular totales presupuestos
      for (let i = 0; i < presupuestosDatos.length; i++) {
        const presupDato = presupuestosDatos[i]

        const asignadosxPresupuestoDato = dataImportes.filter(x => x.codigoPresupuesto === presupDato.codigo)
        const sumaAsignadosxPresupuestoDato = asignadosxPresupuestoDato.reduce(
          (suma, current) => RounderUtil.round(suma + (current.importe || 0)),
          0
        )

        salidaPresupuestoDato[presupDato.codigo] = sumaAsignadosxPresupuestoDato || 0
      }

      setTotalesPeriodos(salidaPeriodos)
      setTotalesPresupuestos(salidaPresupuestoDato)
    })
  }, [obtenerImportesAsignados, infoPeriodos, presupuestosDatos])

  const clickDescargarExcel = React.useCallback(() => {
    __zariContext.loader.open('Descargando reporte')
    PresupuestoAsignacionService.descargarReporteAsignacion({ idProyecto, anoDesde, datoNivel1 })
      .then(({ data, headers }) => {
        DownloadAxiosFile.downloadAsBlob(data, headers)
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
      .finally(() => {
        __zariContext.loader.close()
      })
  }, [__zariContext, idProyecto, anoDesde, datoNivel1])

  React.useEffect(() => {
    obtenerImportesAsignados()
  }, [obtenerImportesAsignados])

  if (!importesAsignadosBd) {
    return null
  }

  return (
    <>
      <Portlet noBg>
        <Portlet.Body>
          <Row>
            <Col md={8} />
            <Col md={2}>
              <Button block onClick={clickTotalizar}>
                Totalizar
              </Button>
            </Col>
            <Col md={2}>
              <Button color='success' block onClick={clickDescargarExcel}>
                Descargar Excel
              </Button>
            </Col>
          </Row>
        </Portlet.Body>
      </Portlet>
      <div className={`${CodigosTransaccionesConstant.PRESUPUESTOS_ASIGNACION}-table-container`}>
        <div className={`${CodigosTransaccionesConstant.PRESUPUESTOS_ASIGNACION}-scroll-wrapper`}>
          <table className={`${CodigosTransaccionesConstant.PRESUPUESTOS_ASIGNACION}-table`}>
            <tr>
              <td className='sticky-col'>CÓDIGO</td>
              <td className='sticky-col'>FASE 1</td>
              <td className='sticky-col'>FASE 2</td>
              <td className='sticky-col'>FASE 3</td>
              <td>TOTAL</td>
              {infoPeriodos.map(x => (
                <td key={x.anoMes}>{x.nombre}</td>
              ))}
            </tr>
            <tr>
              <td className='sticky-col'>&nbsp;</td>
              <td className='sticky-col'>&nbsp;</td>
              <td className='sticky-col'>&nbsp;</td>
              <td className='sticky-col'>&nbsp;</td>
              <td>&nbsp;</td>
              {infoPeriodos.map(x => (
                <td key={x.anoMes}>{totalesPeriodos[x.anoMes] || '--'}</td>
              ))}
            </tr>
            {presupuestosDatos.map(x => (
              <tr key={x.codigo}>
                <td className='sticky-col'>{x.codigo}</td>
                <td className='sticky-col'>
                  <p>{x.descripcionNivel1}</p>
                </td>
                <td className='sticky-col'>
                  <p>{x.descripcionNivel2}</p>
                </td>
                <td className='sticky-col'>
                  <p>{x.descripcionNivel3}</p>
                </td>
                <td>{totalesPresupuestos[x.codigo] || '--'}</td>
                {infoPeriodos.map(y => (
                  <Cells.ImporteCell
                    key={`${x.codigo}${y.anoMes}${idProyecto}`}
                    id={`${x.codigo}${y.anoMes}${idProyecto}`}
                    idProyecto={idProyecto}
                    codigoPresupuesto={x.codigo}
                    anoMes={y.anoMes}
                    importeBd={
                      importesAsignadosBd.find(z => z.anoMes === y.anoMes && z.codigoPresupuesto === x.codigo)
                        ?.importe || ''
                    }
                    limpiarTotales={limpiarTotales}
                  />
                ))}
              </tr>
            ))}
          </table>
        </div>
      </div>
    </>
  )
}

export default React.memo(MainTable)
